import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Save,
  RefreshCw,
  Trash2,
  Undo2,
  Clock,
  Hand,
  AlertTriangle,
  Database,
  Download,
  Users,
  Building2,
  Wrench,
  ChevronDown,
  ChevronUp,
  FileText,
  Gauge,
} from 'lucide-react';
import Card from './Card';
import Button from './Button';
import Select from './Select';
import Input from './Input';
import { apiService } from '../services/apiService';
import { useToast } from '../contexts/ToastContext';

const CONFIRM_WORD = 'RESTAURER';

const formatSizeBytes = (bytes) => {
  if (bytes == null || Number.isNaN(Number(bytes))) return null;
  const n = Number(bytes);
  if (n < 1024) return `${n} o`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} Ko`;
  return `${(n / (1024 * 1024)).toFixed(1)} Mo`;
};

const normalizeBackupStats = (stats) => {
  if (!stats || typeof stats !== 'object') return null;
  const tableStats = stats.tableStats || stats.table_stats || {};
  const rowsFromTables = Object.values(tableStats).reduce(
    (sum, count) => sum + (Number(count) || 0),
    0
  );
  const sizeBytes = stats.sizeBytes ?? stats.size_bytes;

  return {
    ...stats,
    tableStats,
    rows: stats.rows ?? (rowsFromTables || undefined),
    clients: stats.clients ?? stats.clientCount,
    equipment: stats.equipment ?? tableStats.equipment,
    sites: stats.sites ?? tableStats.sites,
    planFiles: stats.planFiles ?? stats.plan_files,
    sizeLabel:
      stats.sizeLabel ??
      stats.size_label ??
      formatSizeBytes(sizeBytes),
  };
};

const formatStatValue = (value) => (value === null || value === undefined ? '—' : value);

const TABLE_LABELS = {
  users: 'Utilisateurs',
  sites: 'Sites',
  equipment: 'Équipements',
  maintenance: 'Maintenances',
  plans: 'Plans',
  notifications: 'Notifications',
  reports: 'Rapports',
  contact_submissions: 'Messages contact',
  cabinet_placements: 'Armoires',
  cabinet_accessories: 'Accessoires armoires',
  equipment_placements: 'Emplacements',
  audit_logs: 'Journaux audit',
};

const BackupRestoreSection = () => {
  const { success: toastSuccess, error: toastError } = useToast();
  const [manualBackups, setManualBackups] = useState([]);
  const [autoBackups, setAutoBackups] = useState([]);
  const [latestBackup, setLatestBackup] = useState(null);
  const [settings, setSettings] = useState({ frequency: 'weekly', lastAutoAt: null, enabled: true });
  const [maxManual, setMaxManual] = useState(10);
  const [maxAuto, setMaxAuto] = useState(10);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [selectedManualKey, setSelectedManualKey] = useState('');
  const [selectedAutoKey, setSelectedAutoKey] = useState('');
  const [restoreKey, setRestoreKey] = useState('');
  const [confirmText, setConfirmText] = useState('');
  const [frequencyDraft, setFrequencyDraft] = useState('weekly');
  const [autoEnabled, setAutoEnabled] = useState(true);
  const [showStats, setShowStats] = useState(false);
  const [lastCreateStats, setLastCreateStats] = useState(null);
  const autoToastShown = useRef(false);

  const loadBackups = useCallback(async ({ silentAutoToast = false } = {}) => {
    try {
      setLoading(true);
      const data = await apiService.listBackups();
      const manual = data.manualBackups || [];
      const auto = data.autoBackups || [];
      setManualBackups(manual);
      setAutoBackups(auto);
      setLatestBackup(data.latestBackup || null);
      setSettings(data.settings || { frequency: 'weekly', lastAutoAt: null, enabled: true });
      setFrequencyDraft(data.settings?.frequency === 'monthly' ? 'monthly' : 'weekly');
      setAutoEnabled(data.settings?.enabled !== false);
      setMaxManual(data.maxManualBackups || 10);
      setMaxAuto(data.maxAutoBackups || 10);

      if (data.autoBackupCreated && !silentAutoToast && !autoToastShown.current) {
        toastSuccess('Sauvegarde automatique créée.');
        autoToastShown.current = true;
      }

      setSelectedManualKey((prev) =>
        manual.some((b) => b.backupKey === prev) ? prev : manual[0]?.backupKey || ''
      );
      setSelectedAutoKey((prev) =>
        auto.some((b) => b.backupKey === prev) ? prev : auto[0]?.backupKey || ''
      );

      const options = [];
      if (data.latestBackup) options.push(data.latestBackup);
      for (const b of manual) {
        if (!options.some((o) => o.backupKey === b.backupKey)) options.push(b);
      }
      for (const b of auto) {
        if (!options.some((o) => o.backupKey === b.backupKey)) options.push(b);
      }
      setRestoreKey((prev) =>
        options.some((b) => b.backupKey === prev) ? prev : options[0]?.backupKey || ''
      );
    } catch (error) {
      toastError(error.message || 'Impossible de charger les sauvegardes');
    } finally {
      setLoading(false);
    }
  }, [toastSuccess, toastError]);

  useEffect(() => {
    loadBackups();
  }, [loadBackups]);

  const handleCreateManual = async () => {
    try {
      setBusy(true);
      const result = await apiService.createBackup();
      setLastCreateStats(result.stats);
      const stats = result.stats || {};
      toastSuccess(
        `Sauvegarde créée — ${stats.rows || 0} lignes, ${stats.clients || 0} clients, ${stats.equipment || 0} équipements, ${stats.planFiles || 0} plans (${stats.sizeLabel || ''})`
      );
      if (result.pruned > 0) {
        toastSuccess(`${result.pruned} ancienne(s) sauvegarde(s) nettoyée(s).`);
      }
      await loadBackups({ silentAutoToast: true });
    } catch (error) {
      toastError(error.message || 'Erreur lors de la sauvegarde');
    } finally {
      setBusy(false);
    }
  };

  const handleDownload = async (backupKey) => {
    if (!backupKey) return;
    try {
      setBusy(true);
      await apiService.downloadBackup(backupKey);
      toastSuccess('Téléchargement démarré');
    } catch (error) {
      toastError(error.message || 'Erreur téléchargement');
    } finally {
      setBusy(false);
    }
  };

  const handleSaveFrequency = async () => {
    try {
      setBusy(true);
      const result = await apiService.updateBackupSettings(frequencyDraft, autoEnabled);
      setSettings(result.settings);
      if (result.autoBackupCreated) {
        toastSuccess('Paramètres enregistrés — première sauvegarde auto créée.');
      } else {
        toastSuccess('Paramètres enregistrés.');
      }
      await loadBackups({ silentAutoToast: true });
    } catch (error) {
      toastError(error.message || 'Erreur paramètres');
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async (backupKey) => {
    if (!backupKey || !window.confirm('Supprimer définitivement cette sauvegarde ?')) return;
    try {
      setBusy(true);
      await apiService.deleteBackup(backupKey);
      toastSuccess('Sauvegarde supprimée');
      await loadBackups({ silentAutoToast: true });
    } catch (error) {
      toastError(error.message || 'Erreur suppression');
    } finally {
      setBusy(false);
    }
  };

  const handleRestore = async () => {
    if (!restoreKey) {
      toastError('Sélectionnez une sauvegarde');
      return;
    }
    if (confirmText !== CONFIRM_WORD) {
      toastError(`Tapez ${CONFIRM_WORD} pour confirmer`);
      return;
    }
    if (!window.confirm('Remplacer toutes les données par cette sauvegarde ?')) return;

    try {
      setBusy(true);
      const result = await apiService.restoreBackup(restoreKey);
      toastSuccess(
        `Restauration OK — ${result.rowsRestored} lignes, ${result.planFilesRestored || 0} fichiers plans. Rechargement…`
      );
      setConfirmText('');
      setTimeout(() => window.location.reload(), 2500);
    } catch (error) {
      toastError(error.message || 'Erreur restauration');
    } finally {
      setBusy(false);
    }
  };

  const formatDate = (iso, { emptyLabel = '—' } = {}) => {
    if (!iso) return emptyLabel;
    try {
      return new Date(iso).toLocaleString('fr-FR');
    } catch {
      return iso;
    }
  };

  const restoreOptions = [];
  if (latestBackup) restoreOptions.push(latestBackup);
  for (const b of manualBackups) {
    if (!restoreOptions.some((o) => o.backupKey === b.backupKey)) restoreOptions.push(b);
  }
  for (const b of autoBackups) {
    if (!restoreOptions.some((o) => o.backupKey === b.backupKey)) restoreOptions.push(b);
  }

  const selectedManual = manualBackups.find((b) => b.backupKey === selectedManualKey);
  const selectedAuto = autoBackups.find((b) => b.backupKey === selectedAutoKey);
  const statsBackup =
    restoreOptions.find((b) => b.backupKey === restoreKey) ||
    selectedManual ||
    selectedAuto ||
    latestBackup;
  const displayStats = normalizeBackupStats(
    lastCreateStats || statsBackup?.stats
  );
  const activeDownloadKey = selectedManualKey || selectedAutoKey || restoreKey;

  const statCards = displayStats
    ? [
        { label: 'Enregistrements', value: displayStats.rows, icon: Database },
        { label: 'Clients', value: displayStats.clients, icon: Users },
        { label: 'Équipements', value: displayStats.equipment, icon: Wrench },
        { label: 'Sites', value: displayStats.sites, icon: Building2 },
        { label: 'Plans (fichiers)', value: displayStats.planFiles, icon: FileText },
        { label: 'Taille', value: displayStats.sizeLabel, icon: Gauge },
      ]
    : [];

  return (
    <div className="space-y-6">
      <div className="flex items-start gap-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <Database className="w-6 h-6 text-blue-600 flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-blue-900">Sauvegardes &amp; Restauration</h3>
          <p className="text-sm text-blue-800 mt-1">
            Sauvegarde complète de la base D1 (clients, équipements, sites, maintenances, rapports,
            messages…) et des fichiers de plans (KV). Jusqu&apos;à {maxManual} versions manuelles et{' '}
            {maxAuto} automatiques — comme DentoExcellance.
          </p>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        <Button variant="outline" icon={RefreshCw} onClick={() => loadBackups({ silentAutoToast: true })} disabled={busy || loading}>
          Actualiser
        </Button>
        <Button variant="primary" icon={Save} onClick={handleCreateManual} loading={busy}>
          Sauvegarder maintenant
        </Button>
        {activeDownloadKey && (
          <Button
            variant="outline"
            icon={Download}
            onClick={() => handleDownload(activeDownloadKey)}
            disabled={busy}
          >
            Télécharger JSON
          </Button>
        )}
      </div>

      {!loading && statCards.length > 0 && (
        <div>
          <p className="text-sm font-medium text-gray-700 mb-2">
            Résumé — {statsBackup?.label || 'sauvegarde sélectionnée'}
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {statCards.map(({ label, value, icon: Icon }) => (
              <div key={label} className="bg-white border border-gray-200 rounded-xl p-3 text-center shadow-sm">
                <Icon className="w-5 h-5 text-red-500 mx-auto mb-1" />
                <p className="text-lg font-bold text-gray-900">{formatStatValue(value)}</p>
                <p className="text-xs text-gray-500">{label}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {!loading && displayStats?.tableStats && Object.keys(displayStats.tableStats).length > 0 && (
        <div>
          <button
            type="button"
            onClick={() => setShowStats(!showStats)}
            className="flex items-center gap-2 text-sm font-medium text-gray-700 hover:text-red-600"
          >
            {showStats ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            Détail par table
          </button>
          {showStats && (
            <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
              {Object.entries(displayStats.tableStats).map(([table, count]) => (
                <div key={table} className="text-xs bg-gray-50 border rounded-lg px-3 py-2 flex justify-between">
                  <span className="text-gray-600 truncate">{TABLE_LABELS[table] || table}</span>
                  <span className="font-bold text-gray-900 ml-2">{count}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-red-500" />
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card>
            <div className="flex items-center gap-2 mb-4">
              <Clock className="w-5 h-5 text-gray-600" />
              <h4 className="font-semibold text-gray-900">Sauvegardes automatiques</h4>
            </div>
            <label className="flex items-center gap-2 mb-4 cursor-pointer">
              <input
                type="checkbox"
                checked={autoEnabled}
                onChange={(e) => setAutoEnabled(e.target.checked)}
                disabled={busy}
                className="rounded border-gray-300 text-red-600 focus:ring-red-500"
              />
              <span className="text-sm text-gray-700">Activer les sauvegardes automatiques</span>
            </label>
            <div className="flex flex-wrap gap-3 items-end mb-4">
              <div className="flex-1 min-w-[140px]">
                <Select
                  label="Fréquence"
                  value={frequencyDraft}
                  onChange={(e) => setFrequencyDraft(e.target.value)}
                  disabled={busy || !autoEnabled}
                  options={[
                    { value: 'weekly', label: 'Hebdomadaire' },
                    { value: 'monthly', label: 'Mensuelle' },
                  ]}
                />
              </div>
              <Button
                variant="primary"
                size="sm"
                onClick={handleSaveFrequency}
                disabled={
                  busy ||
                  (frequencyDraft === settings.frequency && autoEnabled === (settings.enabled !== false))
                }
              >
                Enregistrer
              </Button>
            </div>
            <p className="text-xs text-gray-500 mb-4">
              Dernière auto : {formatDate(settings.lastAutoAt, { emptyLabel: 'Jamais' })}
            </p>
            {autoBackups.length === 0 ? (
              <p className="text-sm text-gray-500 italic">Aucune sauvegarde automatique.</p>
            ) : (
              <>
                <Select
                  label="Version automatique"
                  name="autoBackup"
                  value={selectedAutoKey}
                  onChange={(e) => {
                    setSelectedAutoKey(e.target.value);
                    setLastCreateStats(null);
                  }}
                  disabled={busy}
                  placeholder="Choisir une sauvegarde automatique"
                  options={autoBackups.map((b) => ({
                    value: b.backupKey,
                    label: `${b.label} (${b.stats?.rows || 0} lignes)`,
                  }))}
                />
                {selectedAuto && (
                  <div className="mt-2 space-y-1">
                    <p className="text-xs text-gray-500">{formatDate(selectedAuto.createdAt)}</p>
                    {selectedAuto.stats?.sizeLabel && (
                      <p className="text-xs text-gray-500">Taille : {selectedAuto.stats.sizeLabel}</p>
                    )}
                  </div>
                )}
                <div className="flex gap-2 mt-3">
                  <Button
                    variant="outline"
                    size="sm"
                    icon={Download}
                    onClick={() => handleDownload(selectedAutoKey)}
                    disabled={busy || !selectedAutoKey}
                  >
                    Télécharger
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    icon={Trash2}
                    onClick={() => handleDelete(selectedAutoKey)}
                    disabled={busy || !selectedAutoKey}
                  >
                    Supprimer
                  </Button>
                </div>
              </>
            )}
          </Card>

          <Card>
            <div className="flex items-center gap-2 mb-4">
              <Hand className="w-5 h-5 text-gray-600" />
              <h4 className="font-semibold text-gray-900">Sauvegardes manuelles</h4>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              Créez une version avant une grosse modification ou import de données.
            </p>
            {manualBackups.length === 0 ? (
              <p className="text-sm text-gray-500 italic">
                Aucune sauvegarde manuelle — cliquez sur &quot;Sauvegarder maintenant&quot;.
              </p>
            ) : (
              <>
                <Select
                  label="Version manuelle"
                  name="manualBackup"
                  value={selectedManualKey}
                  onChange={(e) => {
                    setSelectedManualKey(e.target.value);
                    setLastCreateStats(null);
                  }}
                  disabled={busy}
                  placeholder="Choisir une sauvegarde manuelle"
                  options={manualBackups.map((b) => ({
                    value: b.backupKey,
                    label: `${b.label} (${b.stats?.rows || 0} lignes)`,
                  }))}
                />
                {selectedManual && (
                  <div className="mt-2 space-y-1">
                    <p className="text-xs text-gray-500">{formatDate(selectedManual.createdAt)}</p>
                    {selectedManual.stats?.sizeLabel && (
                      <p className="text-xs text-gray-500">Taille : {selectedManual.stats.sizeLabel}</p>
                    )}
                  </div>
                )}
                <div className="flex gap-2 mt-3">
                  <Button
                    variant="outline"
                    size="sm"
                    icon={Download}
                    onClick={() => handleDownload(selectedManualKey)}
                    disabled={busy || !selectedManualKey}
                  >
                    Télécharger
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    icon={Trash2}
                    onClick={() => handleDelete(selectedManualKey)}
                    disabled={busy || !selectedManualKey}
                  >
                    Supprimer
                  </Button>
                </div>
              </>
            )}
          </Card>
        </div>
      )}

      <Card>
        <div className="flex items-center gap-2 mb-3">
          <Undo2 className="w-5 h-5 text-red-600" />
          <h4 className="font-semibold text-gray-900">Restaurer une sauvegarde</h4>
        </div>
        <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg mb-4 flex gap-2">
          <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0" />
          <p className="text-sm text-amber-800">
            La restauration remplace <strong>toutes les données</strong>. Une sauvegarde de sécurité
            est créée automatiquement avant la restauration.
          </p>
        </div>
        {restoreOptions.length > 0 ? (
          <>
            <Select
              label="Sauvegarde à restaurer"
              value={restoreKey}
              onChange={(e) => {
                setRestoreKey(e.target.value);
                setLastCreateStats(null);
              }}
              disabled={busy}
              className="mb-4"
              placeholder="Sélectionner une sauvegarde à restaurer"
              options={restoreOptions.map((b) => ({
                value: b.backupKey,
                label: `${b.label} (${b.stats?.rows ?? 0} lignes)`,
              }))}
            />
            <Input
              label={`Tapez ${CONFIRM_WORD} pour confirmer`}
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder={CONFIRM_WORD}
              disabled={busy}
            />
            <Button
              variant="danger"
              icon={Undo2}
              className="mt-4"
              onClick={handleRestore}
              disabled={busy || !restoreKey || confirmText !== CONFIRM_WORD}
              loading={busy}
            >
              Restaurer cette sauvegarde
            </Button>
          </>
        ) : (
          <p className="text-sm text-gray-500">Créez d&apos;abord une sauvegarde avec le bouton ci-dessus.</p>
        )}
      </Card>
    </div>
  );
};

export default BackupRestoreSection;
